/** Small DOM and formatting helpers shared by the HUD modules. */

/**
 * Colour role of a banner or stamp; maps to the `hud-tone-*` / `hud-stamp-*` classes. `warn` is the
 * red-amber of a refusal - the game saying "that did nothing" - which must not read like a reward.
 */
export type Tone = 'amber' | 'green' | 'red' | 'warn';

/** Creates an element with a class list and optional text. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** The one visibility switch the HUD uses: toggles `hud-hidden`. */
export function show(node: HTMLElement, visible: boolean): void {
  node.classList.toggle('hud-hidden', !visible);
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** `m:ss`, or `mm:ss` when `padMinutes` is set. */
export function mmss(totalSeconds: number, padMinutes = false): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${padMinutes && m < 10 ? '0' : ''}${m}:${r < 10 ? '0' : ''}${r}`;
}

/** Last value written to the DOM per displayed field, so unchanged frames cost no DOM work. */
export class Shown {
  private readonly values = new Map<string, string | number | boolean>();

  /** True when `value` differs from what is on screen for `key` (and records it). */
  changed(key: string, value: string | number | boolean): boolean {
    if (this.values.get(key) === value) return false;
    this.values.set(key, value);
    return true;
  }
}
